import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Draggable from 'react-draggable';
import { Alert, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { API } from '../api/api';
import { END_POINTS } from '../api/endpoints';
import usePermissions from '../utils/permissions';
import { screenBasisPermission } from '../utils/permission';
import { RECORDING_FAILED, RECORDING_POLL_MS, VIDEO_CALL_ERRORS, sdkErrorText } from '../utils/videoCall';
import {
  subscribeAiReviewNotifications,
  getPendingAiReviews,
  getNotificationRedirectPath,
} from '../services/fcmNotificationService';

// The browser run swaps the Agora SDK for a stub; only a build with REACT_APP_E2E set honours that.
const loadAgora = async () => (process.env.REACT_APP_E2E && window.__ONI_E2E_AGORA__) || (await import('agora-rtc-sdk-ng')).default;

// The API gives the patient uid 1 and the provider uid 2; anyone else in the room is the recorder.
const isPatient = (user) => Number(user?.uid) === 1;

const controlSx = (on) => ({
  color: '#fff',
  bgcolor: on ? 'rgba(255,255,255,0.18)' : '#d32f2f',
  transition: 'background-color 0.2s ease, transform 0.15s ease',
  '&:hover': {
    bgcolor: on ? 'rgba(255,255,255,0.3)' : '#b71c1c',
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  '&.Mui-disabled': {
    color: '#fff',
    bgcolor: on ? 'rgba(255,255,255,0.18)' : '#d32f2f',
    opacity: 0.75,
  },
});

// Leave reports still on their way to the API, by appointment. The API decides whether to record a rejoined call
// from the order the leave and the join reach it, so every join waits for the report first. Kept outside the
// component so a call page opened afresh (back to the dashboard, Join again) waits too.
const leaveReports = new Map();
const LEAVE_REPORT_WAIT_MS = 10000; // a report that never answers must not keep the provider out of the call

const reportLeave = (appointmentId) => {
  const report = Promise.race([
    API.post(END_POINTS.videoProviderLeft(appointmentId)),
    new Promise((resolve) => setTimeout(resolve, LEAVE_REPORT_WAIT_MS)),
  ]).finally(() => {
    if (leaveReports.get(appointmentId) === report) leaveReports.delete(appointmentId);
  });
  leaveReports.set(appointmentId, report);
};

const errorText = (res) => VIDEO_CALL_ERRORS[res?.errorCode] ?? res?.message ?? 'Could not join the call.';

const closeCall = async ({ client, tracks }) => {
  tracks.forEach((t) => { t.stop(); t.close(); });
  if (client) await client.leave().catch(() => {});
};

/** Provider side of a Video Consultation: joins the Call Room, shows the patient, can leave and rejoin. */
const VideoCall = ({
  appointmentId: propAppointmentId,
  patientName: propPatientName,
  patientId: propPatientId,
  consultationId: propConsultationId,
  isModal = false,
  onClose,
  onCallLeft,
}) => {
  const { appointmentId: paramAppointmentId } = useParams();
  const navigate = useNavigate();
  const locationState = useLocation().state ?? {}; // absent after a page reload
  
  const appointmentId = propAppointmentId || paramAppointmentId;
  const patientName = propPatientName || locationState.patientName;
  const patientId = propPatientId || locationState.patientId;
  const consultationId = propConsultationId || locationState.consultationId;
  const [phase, setPhase] = useState('joining'); // joining | joined | left | error
  const [error, setError] = useState('');
  const [patientLeft, setPatientLeft] = useState(false);
  const [patientHere, setPatientHere] = useState(false);
  const [patientCamOn, setPatientCamOn] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [windowClosing, setWindowClosing] = useState(false);
  const [recordingFailed, setRecordingFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [aiNotesReady, setAiNotesReady] = useState(false);
  const isExpanded = isFullscreen || isMaximized;

  useEffect(() => {
    if (!consultationId) return;
    const unsubscribe = subscribeAiReviewNotifications((reviews) => {
      const isReady = reviews?.some((r) => r.consultationId === consultationId);
      setAiNotesReady(Boolean(isReady));
    });
    return () => unsubscribe();
  }, [consultationId]);

  const mounted = useRef(true);
  const roomRequest = useRef(0); // numbers every room read and retry; only the latest may set the banner
  const polling = useRef(false);
  const retryBusy = useRef(false);
  const call = useRef({ client: null, tracks: [] });
  const attempt = useRef(0); // bumped by every leave, so a join still in flight knows it was abandoned
  const busy = useRef(false); // one join at a time
  const leaving = useRef(false); // one leave at a time
  const micSwitching = useRef(false);
  const camSwitching = useRef(false);
  const { hasPermission } = usePermissions();
  const localEl = useRef(null);
  const remoteEl = useRef(null);
  const dragNodeRef = useRef(null);
  const fullscreenContainerRef = useRef(null);

  const leave = useCallback(async () => {
    attempt.current += 1;
    const current = call.current;
    call.current = { client: null, tracks: [] };
    await closeCall(current);
  }, []);

  const join = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    const mine = { client: null, tracks: [] };
    call.current = mine;
    const id = ++attempt.current;
    const abandoned = async () => {
      if (id === attempt.current) return false;
      await closeCall(mine);
      return true;
    };
    setPhase('joining');
    setError('');
    setPatientLeft(false);
    setPatientHere(false);
    setPatientCamOn(false);
    setMicOn(true);
    setCamOn(true);
    setWindowClosing(false);
    try {
      await leaveReports.get(appointmentId);
      const token = await API.post(END_POINTS.videoJoinToken(appointmentId));
      if (await abandoned()) return;
      if (!token?.success) {
        setError(errorText(token));
        setPhase('error');
        return;
      }
      const sdk = await loadAgora();
      const client = sdk.createClient({ mode: 'rtc', codec: 'vp8' });
      mine.client = client;
      const patientIsHere = () => { setPatientHere(true); setPatientLeft(false); };
      client.on('user-joined', (user) => isPatient(user) && patientIsHere());
      client.on('user-published', async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        patientIsHere();
        if (mediaType === 'video') {
          setPatientCamOn(true);
          // The patient is on a phone, so the video is portrait: show all of it instead of cropping it to the box.
          user.videoTrack.play(remoteEl.current, { fit: 'contain' });
        }
        if (mediaType === 'audio') user.audioTrack.play();
      });
      client.on('user-unpublished', (user, mediaType) => isPatient(user) && mediaType === 'video' && setPatientCamOn(false));
      client.on('user-left', (user) => {
        if (!isPatient(user)) return;
        setPatientLeft(true);
        setPatientHere(false);
        setPatientCamOn(false);
      });
      // The token lasts to the end of the join window and cannot be renewed past it.
      client.on('token-privilege-will-expire', () => setWindowClosing(true));
      const { appId, channelName, token: rtcToken, uid } = token.data;
      await client.join(appId, channelName, rtcToken, uid);
      if (await abandoned()) return;
      const [mic, cam] = await sdk.createMicrophoneAndCameraTracks();
      mine.tracks = [mic, cam];
      if (await abandoned()) return;
      cam.play(localEl.current);
      await client.publish([mic, cam]);
      if (await abandoned()) return;
      setPhase('joined');
      // Fallback for the recording start when Agora's channel notifications are off; the server ignores repeats.
      API.post(END_POINTS.videoProviderJoined(appointmentId));
    } catch (e) {
      await closeCall(mine);
      if (id === attempt.current) {
        setError(sdkErrorText(e));
        setPhase('error');
      }
    } finally {
      busy.current = false;
    }
  }, [appointmentId]);

  useEffect(() => {
    join();
    return () => { leave(); };
  }, [join, leave]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Watch the room's recording state while in the call. The call itself is never touched by what this finds.
  useEffect(() => {
    if (phase !== 'joined') return undefined;
    const check = async () => {
      if (polling.current) return; // a slow read must not stack up behind itself
      polling.current = true;
      const request = ++roomRequest.current;
      const room = await API.get(END_POINTS.videoRoom(appointmentId));
      polling.current = false;
      if (mounted.current && request === roomRequest.current && room?.success) {
        const failed = room.data?.recordingStatus === RECORDING_FAILED;
        setRecordingFailed(failed);
        if (!failed) setRetryError('');
      }
    };
    check();
    const timer = setInterval(check, RECORDING_POLL_MS);
    return () => {
      clearInterval(timer);
      roomRequest.current += 1; // drop any read still in flight
      setRecordingFailed(false);
    };
  }, [phase, appointmentId]);

  const retryRecording = async () => {
    if (retryBusy.current) return;
    retryBusy.current = true;
    setRetrying(true);
    setRetryError('');
    const request = ++roomRequest.current;
    const res = await API.post(END_POINTS.videoRecordingRetry(appointmentId));
    retryBusy.current = false;
    if (!mounted.current) return;
    setRetrying(false);
    if (!res?.success) {
      setRetryError(VIDEO_CALL_ERRORS[res?.errorCode] ?? res?.message ?? 'Could not restart the recording.');
    } else if (request === roomRequest.current) {
      const failed = res.data?.recordingStatus === RECORDING_FAILED;
      setRecordingFailed(failed);
      // The server answers 201 even when it had nothing to restart (for example the patient has not consented).
      if (failed) setRetryError('The recording is still not running.');
    }
  };

  // Only this button reports the leave: it tells the API to process the recording so far. Leaving the page or a
  // dropped connection does not, and the recording runs on.
  const onLeave = async () => {
    if (leaving.current) return;
    leaving.current = true;
    try {
      await leave();
      reportLeave(appointmentId);
      if (isModal && onCallLeft) {
        onCallLeft();
      } else {
        setPhase('left');
      }
    } finally {
      leaving.current = false;
    }
  };

  const toggleFullScreen = async () => {
    const isCurrentlyFull = Boolean(document.fullscreenElement) || isMaximized;
    if (isCurrentlyFull) {
      if (document.fullscreenElement) {
        try {
          if (document.exitFullscreen) await document.exitFullscreen();
          else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
        } catch (e) {
          // ignore
        }
      }
      setIsFullscreen(false);
      setIsMaximized(false);
    } else {
      const target = isModal ? dragNodeRef.current : (fullscreenContainerRef.current || document.documentElement);
      if (target && document.fullscreenEnabled) {
        try {
          if (target.requestFullscreen) {
            await target.requestFullscreen();
          } else if (target.webkitRequestFullscreen) {
            await target.webkitRequestFullscreen();
          }
        } catch (e) {
          // fallback to maximized viewport
        }
      }
      setIsMaximized(true);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const active = Boolean(document.fullscreenElement);
      setIsFullscreen(active);
      if (!active) {
        setIsMaximized(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && (isMaximized || isFullscreen)) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
        setIsMaximized(false);
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMaximized, isFullscreen]);

  // Mute keeps the microphone open and sends silence; turning the camera off releases it, so its light goes out.
  // Performs optimistic state updates and uses internal refs to prevent button flickering and disappearing.
  const toggle = async (kind) => {
    const [mic, cam] = call.current.tracks;
    if (kind === 'mic') {
      if (micSwitching.current || !mic) return;
      micSwitching.current = true;
      const targetMic = !micOn;
      setMicOn(targetMic);
      try {
        await mic.setMuted(!targetMic);
      } catch (e) {
        if (mounted.current) setMicOn(!targetMic);
      } finally {
        micSwitching.current = false;
      }
    } else {
      if (camSwitching.current || !cam) return;
      camSwitching.current = true;
      const targetCam = !camOn;
      setCamOn(targetCam);
      try {
        await cam.setEnabled(targetCam);
      } catch (e) {
        if (mounted.current) setCamOn(!targetCam);
      } finally {
        camSwitching.current = false;
      }
    }
  };

  const status =
    phase === 'joining' ? 'Joining the call...'
      : phase === 'left' ? 'You left the call'
        : phase !== 'joined' ? ''
          : patientLeft ? `${patientName || 'The patient'} left the call`
            : !patientHere ? `Waiting for ${patientName || 'the patient'} to join...`
              : !patientCamOn ? `${patientName ? `${patientName}'s` : "The patient's"} camera is off`
                : '';

  const content = (
    <Box
      ref={fullscreenContainerRef}
      sx={{
        p: isModal ? (isExpanded ? 1.5 : 1) : (isExpanded ? 1 : 2),
        display: 'flex',
        flexDirection: 'column',
        height: isModal ? (isExpanded ? 'calc(100vh - 44px)' : '500px') : (isExpanded ? '100vh' : 'calc(100vh - 64px)'),
        width: isModal ? (isExpanded ? '100%' : '350px') : 'auto',
        bgcolor: isModal || isExpanded ? '#111' : 'transparent',
        boxSizing: 'border-box',
        flex: isModal && isExpanded ? 1 : undefined,
      }}
      data-testid="video-call-page"
      data-phase={phase}
    >
      {!isModal && !isExpanded && (
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'left' }}>Video consultation</Typography>
          {(phase === 'left' || phase === 'error') && <Button variant="contained" data-testid="rejoin-call" onClick={join}>{phase === 'left' ? 'Rejoin' : 'Try again'}</Button>}
          <Button variant="text" onClick={() => navigate('/dashboard')}>Back to dashboard</Button>
        </Stack>
      )}
      {phase === 'error' && <Alert severity="error" data-testid="call-error" sx={{ mb: 2 }}>{error}</Alert>}
      {phase === 'left' && (
        <Alert
          severity={aiNotesReady ? 'success' : 'info'}
          icon={aiNotesReady ? <AutoAwesomeIcon sx={{ color: '#8A4BFF' }} /> : undefined}
          sx={{
            mb: 2,
            ...(aiNotesReady && {
              bgcolor: '#F5F3FF',
              border: '1px solid #D8B4FE',
              color: '#3B0764',
            }),
          }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              {aiNotesReady && (
                <Button
                  variant="contained"
                  size="small"
                  data-testid="review-ai-cards-btn"
                  onClick={() => {
                    const review = (getPendingAiReviews() || []).find((r) => r.consultationId === consultationId);
                    const route = getNotificationRedirectPath(
                      review || { consultationId, patientId, consultationStatus: 'IN_PROGRESS' }
                    );
                    navigate(route);
                  }}
                  sx={{
                    bgcolor: '#8A4BFF',
                    color: '#FFF',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#7434F5' },
                  }}
                >
                  Review AI Notes
                </Button>
              )}
              {consultationId && patientId && (
                <Button
                  color="inherit"
                  size="small"
                  data-testid="open-consultation"
                  onClick={() => screenBasisPermission(consultationId, patientId, 'edit', hasPermission, navigate)}
                >
                  Open consultation
                </Button>
              )}
            </Stack>
          }
        >
          {aiNotesReady ? (
            <>
              <strong>AI notes are ready!</strong> Clinical consultation cards have been extracted and are waiting for your review.
            </>
          ) : (
            <>
              You left the call. What was recorded so far is being processed for the consultation notes.{' '}
              {consultationId && patientId ? 'Rejoin, or open the consultation to finish it.' : 'Rejoin, or carry on with the consultation from the dashboard.'}
            </>
          )}
        </Alert>
      )}
      {phase === 'joined' && recordingFailed && (
        <Alert
          severity="warning"
          data-testid="recording-banner"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" data-testid="retry-recording" disabled={retrying} onClick={retryRecording}>Retry recording</Button>}
        >
          This call is not being recorded. The consultation can carry on with manual notes, or you can retry the recording.
          {retryError && <Typography variant="body2" data-testid="retry-error">{retryError}</Typography>}
        </Alert>
      )}
      {phase === 'joined' && windowClosing && <Alert severity="warning" data-testid="window-closing" sx={{ mb: 2 }}>The join window is about to close and the call will end. Finish up and end the consultation from the consultation page.</Alert>}
      {phase === 'joined' && patientLeft && <Alert severity="info" data-testid="patient-left" sx={{ mb: 2 }}>The patient left the call. They can rejoin while the join window is open.</Alert>}
      <Box sx={{ position: 'relative', flex: 1, minHeight: 320, bgcolor: '#000', borderRadius: 2, overflow: 'hidden', display: phase === 'error' ? 'none' : 'block' }}>
        <Box ref={remoteEl} data-testid="remote-video" sx={{ position: 'absolute', inset: 0 }} />
        {status && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, pointerEvents: 'none' }}>
            <Typography data-testid="call-status" sx={{ color: '#fff', fontSize: isModal && !isExpanded ? 15 : 18, textAlign: 'center' }}>{status}</Typography>
          </Box>
        )}
        {patientName && phase === 'joined' && (
          <Typography sx={{ position: 'absolute', top: 12, left: 12, px: 1.25, py: 0.5, color: '#fff', bgcolor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', borderRadius: 1.5, fontSize: isModal && !isExpanded ? 12 : 13, fontWeight: 500 }}>
            {patientName}
          </Typography>
        )}
        {!isModal && isExpanded && (
          <Tooltip title="Exit full screen (Esc)" arrow enterDelay={200}>
            <IconButton
              onClick={toggleFullScreen}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.65)',
                backdropFilter: 'blur(4px)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                zIndex: 10,
              }}
            >
              <FullscreenExitIcon />
            </IconButton>
          </Tooltip>
        )}
        <Box
          sx={{
            position: 'absolute',
            right: 16,
            bottom: 16,
            width: isModal && !isExpanded ? 80 : { xs: 120, md: 200 },
            height: isModal && !isExpanded ? 100 : { xs: 90, md: 150 },
            bgcolor: '#222',
            borderRadius: 1.5,
            overflow: 'hidden',
            border: '2px solid rgba(255,255,255,0.35)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            display: phase === 'joined' ? 'block' : 'none',
            zIndex: 10,
            transition: 'width 0.2s ease, height 0.2s ease',
          }}
        >
          <Box ref={localEl} data-testid="local-video" sx={{ width: '100%', height: '100%' }} />
          {!camOn && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#222' }}>
              <Typography sx={{ color: '#bbb', fontSize: isModal && !isExpanded ? 10 : 12, textAlign: 'center', px: 1 }}>Your camera is off</Typography>
            </Box>
          )}
        </Box>
        {phase === 'joined' && (
          <Stack
            direction="row"
            spacing={isModal && !isExpanded ? 1 : 1.5}
            alignItems="center"
            sx={{
              position: 'absolute',
              left: '50%',
              bottom: 16,
              transform: 'translateX(-50%)',
              p: isModal && !isExpanded ? 0.75 : 1,
              bgcolor: 'rgba(0,0,0,0.65)',
              backdropFilter: 'blur(8px)',
              borderRadius: 6,
              zIndex: 10,
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            }}
          >
            <Tooltip title={micOn ? 'Mute microphone' : 'Unmute microphone'} arrow enterDelay={200} leaveDelay={0}>
              <IconButton
                data-testid="toggle-mic"
                aria-label={micOn ? 'Mute microphone' : 'Unmute microphone'}
                aria-pressed={!micOn}
                onClick={() => toggle('mic')}
                sx={{
                  ...controlSx(micOn),
                  width: isModal && !isExpanded ? 32 : 40,
                  height: isModal && !isExpanded ? 32 : 40,
                }}
              >
                {micOn ? <MicIcon fontSize={isModal && !isExpanded ? 'small' : 'medium'} /> : <MicOffIcon fontSize={isModal && !isExpanded ? 'small' : 'medium'} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={camOn ? 'Turn camera off' : 'Turn camera on'} arrow enterDelay={200} leaveDelay={0}>
              <IconButton
                data-testid="toggle-camera"
                aria-label={camOn ? 'Turn camera off' : 'Turn camera on'}
                aria-pressed={!camOn}
                onClick={() => toggle('camera')}
                sx={{
                  ...controlSx(camOn),
                  width: isModal && !isExpanded ? 32 : 40,
                  height: isModal && !isExpanded ? 32 : 40,
                }}
              >
                {camOn ? <VideocamIcon fontSize={isModal && !isExpanded ? 'small' : 'medium'} /> : <VideocamOffIcon fontSize={isModal && !isExpanded ? 'small' : 'medium'} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={isExpanded ? 'Exit full screen (Esc)' : 'Full screen'} arrow enterDelay={200} leaveDelay={0}>
              <IconButton
                data-testid="toggle-fullscreen"
                aria-label={isExpanded ? 'Exit full screen' : 'Full screen'}
                onClick={toggleFullScreen}
                sx={{
                  ...controlSx(true),
                  width: isModal && !isExpanded ? 32 : 40,
                  height: isModal && !isExpanded ? 32 : 40,
                }}
              >
                {isExpanded ? <FullscreenExitIcon fontSize={isModal && !isExpanded ? 'small' : 'medium'} /> : <FullscreenIcon fontSize={isModal && !isExpanded ? 'small' : 'medium'} />}
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="error"
              data-testid="leave-call"
              onClick={onLeave}
              sx={{
                borderRadius: 5,
                textTransform: 'none',
                minWidth: isModal && !isExpanded ? 'auto' : 64,
                px: isModal && !isExpanded ? 1.5 : 2,
                height: isModal && !isExpanded ? 32 : 40,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#b71c1c' },
              }}
            >
               {isModal && !isExpanded ? <CallEndIcon fontSize="small" /> : <><CallEndIcon sx={{ mr: 1 }} /> Leave call</>}
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );

  if (isModal) {
    return (
      <Draggable
        handle=".drag-handle"
        nodeRef={dragNodeRef}
        disabled={isExpanded}
        position={isExpanded ? { x: 0, y: 0 } : dragPosition}
        onDrag={(e, data) => setDragPosition({ x: data.x, y: data.y })}
      >
        <Box
          ref={dragNodeRef}
          sx={{
            position: 'fixed',
            ...(isExpanded
              ? {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100vw !important',
                  height: '100vh !important',
                  borderRadius: 0,
                  m: 0,
                  transform: 'none !important',
                }
              : {
                  bottom: 20,
                  right: 20,
                  width: 350,
                  height: 542,
                  borderRadius: 2,
                }),
            zIndex: 9999,
            overflow: 'hidden',
            boxShadow: isExpanded ? 24 : 10,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#111',
            transition: 'width 0.2s ease, height 0.2s ease, border-radius 0.2s ease',
          }}
        >
          <Box
            className="drag-handle"
            sx={{
              cursor: isExpanded ? 'default' : 'move',
              bgcolor: '#262626',
              color: '#fff',
              px: 1.5,
              py: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              userSelect: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', letterSpacing: '0.2px' }}>
              {patientName || 'Video Consultation'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={isExpanded ? 'Exit full screen (Esc)' : 'Full screen'} arrow enterDelay={200}>
                <IconButton size="small" onClick={toggleFullScreen} sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                  {isExpanded ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close window" arrow enterDelay={200}>
                <IconButton size="small" onClick={onClose} sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {content}
        </Box>
      </Draggable>
    );
  }

  return content;
};

export default VideoCall;
